import React, { useCallback, useContext, useState } from 'react';
import { Card, PageHeader, SectionTitle } from '../design-system';
import UserProfileContext from '../context/userprofilecontext.js';
import { apiErrorDetails, useLessonProposals } from '../api/lessonreview';
import ProposalTable from '../components/lessonreview/ProposalTable';
import ProposalDrawer from '../components/lessonreview/ProposalDrawer';
import FirmLessonsPanel from '../components/lessonreview/FirmLessonsPanel';
import CentralPanel from '../components/lessonreview/CentralPanel';
import ReviewWithLiviButton from '../components/lessonreview/ReviewWithLiviButton';
import styles from '../components/lessonreview/LessonReview.module.css';

const TABS = [
  { id: 'queue', label: 'Waiting for review' },
  { id: 'firm', label: 'Firm lessons' },
  { id: 'central', label: 'Livisibly-wide' },
  { id: 'rejected', label: 'Rejected' },
];

function ProposalList({ status, enabled, onSelect, emptyText, showReason }) {
  const { data, error, isLoading } = useLessonProposals(status, enabled);
  if (error) {
    return (
      <div className={styles.errorBanner} role="alert">
        {apiErrorDetails(error, 'The list could not be loaded.').message}
      </div>
    );
  }
  if (isLoading) return <p className={styles.emptyText}>Loading…</p>;
  return (
    <ProposalTable proposals={data?.proposals || []} onSelect={onSelect}
                   emptyText={emptyText} showReason={showReason} />
  );
}

/**
 * Livi lessons (internal) — super-admin review of lessons Livi learned or users
 * suggested (Learning Loop 1.3). Firm lessons go live on approval; Livisibly-wide
 * lessons are staged for the next release.
 */
export default function LessonReviewPage() {
  const userProfileContext = useContext(UserProfileContext);
  const isSuperAdmin = Boolean(userProfileContext?.userProfile?.isSuperAdmin);
  const [tab, setTab] = useState('queue');
  const [openProposalId, setOpenProposalId] = useState(null);
  const openProposal = useCallback((p) => setOpenProposalId(p.lesson_proposal_id), []);
  const closeProposal = useCallback(() => setOpenProposalId(null), []);

  if (!isSuperAdmin) {
    return (
      <div className={styles.page}>
        <PageHeader title="Livi Lessons" subtitle="Review of lessons Livi applies across cases" />
        <Card padding="md">
          <p className={styles.emptyText}>This page is restricted to super admins.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Livi Lessons"
                  subtitle="Review the lessons Livi applies across a firm's cases or across all of Livisibly"
                  actions={<ReviewWithLiviButton label="Review the queue with Livi" />} />

      <div className={styles.tabs} role="tablist" aria-label="Lesson review sections">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" id={`lesson-tab-${t.id}`}
                  aria-selected={tab === t.id} aria-controls={`lesson-panel-${t.id}`}
                  className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
                  onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`lesson-panel-${tab}`} aria-labelledby={`lesson-tab-${tab}`}
           className={styles.section}>
        {tab === 'queue' ? (
          <section className={styles.section}>
            <SectionTitle>Waiting for review</SectionTitle>
            <p className={styles.intro}>
              Suggested by users through Livi (or written by a super-admin). Nothing here affects any case
              until it is approved.
            </p>
            <ProposalList status="PENDING" enabled onSelect={openProposal}
                          emptyText="Nothing is waiting for review." />
          </section>
        ) : null}
        {tab === 'firm' ? <FirmLessonsPanel enabled /> : null}
        {tab === 'central' ? <CentralPanel enabled onSelectProposal={openProposal} /> : null}
        {tab === 'rejected' ? (
          <section className={styles.section}>
            <SectionTitle>Rejected</SectionTitle>
            <p className={styles.intro}>Kept with their reasons so similar suggestions can be recognized.</p>
            <ProposalList status="REJECTED" enabled onSelect={openProposal} showReason
                          emptyText="No rejected suggestions." />
          </section>
        ) : null}
      </div>

      {openProposalId ? <ProposalDrawer lessonProposalId={openProposalId} onClose={closeProposal} /> : null}
    </div>
  );
}
